
import { Vue, Component } from "vue-property-decorator"
import PlanningJobsQuery from "@/graphql/job/planningjobs.graphql"
import AbsenceReports from "@/graphql/workreport/absencereports.graphql"
import PublicHolidays from "@/graphql/calendar/publicHolidays.graphql"
import EntityEventSubscription from "@/graphql/subscription/entityevent.graphql"
import { CircularLoader, Require } from "@coredination/components"
import { Route } from "vue-router"
import moment from "moment"
import * as cdn from "@coredination/components"
import { User } from "@coredination/common"
import { navbar, NavbarOption } from "@/components/NavbarOptions.vue"

@Component({
    components: {
        Calendar: () => import("@/components/misc/Calendar.vue"),
        CircularLoader,
        Require,
    },
    apollo: {
        me: cdn.graphql.me,
        jobs: {
            query: PlanningJobsQuery,
            pollInterval: 300000,
            skip() {
                return !this.from || !this.to
            },
            variables() {
                var params: any = {
                    from: this.from,
                    to: this.to,
                    states: this.defaultJobStates,
                    filterParams: this.computedFilterParams,
                    hideOldFinished: true
                }
                return params
            },
        },
        absenceReports: {
            query: AbsenceReports,
            pollInterval: 300000,
            skip() {
                return !this.from || !this.to
            },
            variables() {
                var midnight = new Date()
                midnight.setHours(0, 0, 0, 0)
                var params: any = {
                    from: this.calendar ? this.calendar.calendarType == 'listView' ? midnight : this.from : this.from,
                    to: this.to,
                    filterParams: this.computedFilterParams
                }
                return params
            },
        },
        publicHolidays: {
            query: PublicHolidays,
            variables() {
                var currentYear = new Date().getFullYear().toString()
                var params: any = {
                    year: currentYear,
                }
                return params
            },
        },
        $subscribe: {
            entityEvent: {
                query: EntityEventSubscription,
                result(result: any) {
                    const event = result.data.entityEvent
                    if (this.shouldRefresh(event)) {
                        this.refresh()
                        return
                    }
                },
                skip() {
                    return this.inhibitSubscription
                }
            }
        }
    }
})
export default class PlanningView extends Vue {
    jobs: any[] = []
    absenceReports: any[] = []
    publicHolidays: any[] = []
    from?: Date | null = null
    to?: Date | null = null
    me?: User

    // TODO load from user conf maybe? like how frontend currently does it
    // default
    listTypeFilter: any = {
        type: "list",
        value: "mine",
    }

    defaultJobStates: string[] = [
        "INITIAL",
        "DRAFT",
        "PUBLISHED",
        "ASSIGNED",
        "SCHEDULED",
        "IN_TRANSIT",
        "ON_STATION",
        "STARTED",
        "PAUSED",
        "DAY_FINISHED",
        "FINISHED",
        "CANCELED",
        "CLOSED"
    ]

    get inhibitSubscription() {
        return localStorage && !!localStorage.getItem("inhibitSubscription")
    }

    get fabOptions() {
        return [
            {
                title: this.$t("createNewJob"),
                to: `/job/new?date=${this.date}` + this.meParam,
                if: () => this.me && this.me.hasFeature("jobs") && this.me?.hasPermissionInPrimaryGroup("create_jobs") && this.me?.hasPermission("create_jobs")
            },
            {
                title: this.$t(this.isFuture ? "planAbsence" : "reportAbsence"),
                to: `/workreport/absence/new?date=${this.date}`,
                if: () => this.me && this.me.hasFeature("absence") && this.me?.hasPermissionInPrimaryGroup("do_work")
            }
        ]
    }

    mounted() {
        if (sessionStorage && 'planningView' in sessionStorage) {
            var stored: any = JSON.parse(sessionStorage['planningView'])
            if (stored.listTypeFilter)
                this.listTypeFilter = stored.listTypeFilter
            if (stored.listTypeFilter.type! == 'user' && stored.listTypeFilter.name)
                Vue.set(Vue.prototype, '$appHeader',  stored.listTypeFilter.name)
            else if (stored.listTypeFilter.type! == 'list' && stored.listTypeFilter.value)
                Vue.set(Vue.prototype, '$appHeader', this.$t('planningFilter_listType_' + stored.listTypeFilter.value))
        }
        navbar?.setOptions(this.options)
    }

    get options(): NavbarOption[] {
        return [{
            icon: "refresh",
            action: this.refresh
        }]
    }

    get loading() {
        return this.$apollo.loading
    }

    shouldRefresh(entityEvent: any) {
        let entity: any = entityEvent.data
        let entityClass: string = entity.entityClass
        if (!['WorkReport', 'Job'].includes(entityClass))
            return false
        // should always refresh if job/report right? i guess the filter params should be applied but how?
        return true
    }

    refresh() {
        this.$apollo.queries.jobs.refetch()
        this.$apollo.queries.absenceReports.refetch()
        if (this.calendar.refresh) this.calendar.refresh()
    }

    mobileRefresh() {
        this.$apollo.queries.jobs.refetch()
        this.$apollo.queries.absenceReports.refetch()
    }

    changeDate(from: Date, to: Date) {
        var changed: boolean = false
        if (this.from != from) {
            this.from = from
            changed = true
        }
        if (this.to != to) {
            this.to = to
            changed = true
        }
        if (changed) this.refresh()
    }

    changeFilter(filter: any) {
        this.listTypeFilter = filter
        if (sessionStorage) {
            var stored: any = {}
            if ('planningView' in sessionStorage)
                stored = JSON.parse(sessionStorage['planningView'])
            stored['listTypeFilter'] = this.listTypeFilter
            sessionStorage['planningView'] = JSON.stringify(stored)
        }
        if (filter?.type == 'user' && filter?.name) Vue.set(Vue.prototype, '$appHeader',  filter.name)
        else if (filter?.type == 'list' && filter?.value) Vue.set(Vue.prototype, '$appHeader', this.$t('planningFilter_listType_' + filter?.value)) 
        this.refresh()
    }

    get calendar() {
        return this.$refs.calendar as Vue & { refresh: () => boolean }
    }

    get meParam() {
        return this.listTypeFilter?.value == "mine" ? "&me" : ""
    }

    get date() {
        return moment(this.from).format("YYYY-MM-DD")
    }

    get isFuture() {
        return this.from && this.from.getTime() > new Date().getTime()
    }

    get computedFilterParams() {
        var filterParams: any = Object.assign({}, this.listTypeFilter)
        if ('name' in filterParams)
            delete filterParams.name
        return filterParams
    }

    iosWrapperClient() {
        return Vue.prototype.$iosWrapperClient
    }
}
